import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import JSONPretty from 'react-json-pretty';
import { useSearchParams } from 'react-router-dom';

import OcppLogsProperties from '../../interface/device-ocpp-logs.interface';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { CapitalizeFirstLetter } from '../../shared-functions/StringFunctions';

interface OcppLogSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface OcppMessageProperties {
  messageTypeId: string | number;
  uniqueId: string;
  action?: string;
  payload: any;
}

/**
 * Station activity side panel
 * @returns
 */
function OcppLogSidePanel(properties: OcppLogSidePanelProperties) {
  const { t } = useTranslation('stationManagement');
  const { fetchIdFrom, id } = properties;
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [ocppLogData, setOcppLogData] = useState<OcppLogsProperties>();
  const [dataToDisplay, setDataToDisplay] = useState<OcppMessageProperties>();
  const { stationsActivity } = useAppSelector((state: RootState) => state.stationActivity);

  /**
   * Function to split the message string as object
   * @param messageString
   */
  const parseOcppMessage = (messageString: string) => {
    try {
      const parsed = JSON.parse(messageString) as [number, string, string | object, object?];
      const [messageTypeId, uniqueId, third, fourth] = parsed;

      // third is payload, no action
      if (typeof third === 'object') {
        setDataToDisplay({
          messageTypeId,
          uniqueId,
          payload: third,
        });
      } else {
        // third is action, fourth should be payload
        setDataToDisplay({
          messageTypeId,
          uniqueId,
          action: third,
          payload: fourth ?? {}, // fallback if fourth is missing
        });
      }
    } catch (error) {
      console.error('Invalid OCPP message string:', error);
    }
  };

  /**
   * Function for fetching station activity details
   * @param stationId
   */
  const handleFetchOcppDetails = (logId: string) => {
    const logData = stationsActivity.find((log) => log.id === logId);

    if (!logData) {
      searchParameters.delete('stationId');
      setSearchParameters(searchParameters);
    }

    setOcppLogData(logData);
    parseOcppMessage(logData?.message || '');
  };

  useEffect(() => {
    let logId;
    if (fetchIdFrom === 'queryParams') {
      logId = searchParameters.get('stationId');
    } else if (fetchIdFrom === 'props') {
      logId = id;
    }

    if (!logId) return;

    handleFetchOcppDetails(logId);
  }, [searchParameters, id]);
  return (
    <div className="ocpp-logs-side-panel-wrap">
      <div className="each-log-data">
        <h6>{t('messageType')}</h6>
        <p className={ocppLogData?.type}>{CapitalizeFirstLetter(ocppLogData?.type || '-')}</p>
      </div>
      <div className="each-log-data">
        <h6>{t('messageTypeId')}</h6>
        <p>{dataToDisplay?.messageTypeId || '-'}</p>
      </div>
      <div className="each-log-data">
        <h6>{t('uniqueId')}</h6>
        <p>{dataToDisplay?.uniqueId || '-'}</p>
      </div>
      <div className="each-log-data">
        <h6>{t('action')}</h6>
        <p>{ocppLogData?.action || '-'}</p>
      </div>
      <div className="each-log-data payload">
        <h6>{t('payload')} :</h6>
        <p>
          <JSONPretty id="json-pretty" data={dataToDisplay?.payload} />
        </p>
      </div>
    </div>
  );
}

export default OcppLogSidePanel;
